<template>
  <div>
    <app-cells>
      <div class="date-picker-wrapper">
        <VueCtkDateTimePicker
          id="date_picker_balance"
          class="custom-date-time-picker custom-date-time-picker--range"
          v-model="filter_dates"
          :custom-shortcuts="custom_shortcuts"
          shortcut="thisYear"
          @is-hidden="onIsHiddenPicker"
          format="YYYY-MM-DD"
          formatted="ll"
          label="Select dates"
          color="#77b017"
          button-color="#77b017"
          overlay
          no-header
          no-label
          locale="en"
          range
          noClearButton
        />
        <div v-if="date_picker_error" class="date-picker-wrapper__error">Choose an end date</div>
      </div>
    </app-cells>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells><h2 class="title">TZS compensation</h2></app-cells>
      <div class="charts-grid">
        <highcharts :options="graph_options" />
      </div>
      <app-cells><h2 class="title">Advances compensation</h2></app-cells>
      <div class="charts-grid">
        <highcharts :options="graph_options_advances" />
      </div>
      <app-cells><h2 class="title">Advances list</h2></app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__employee><div class="table-default__left">Employee</div></template>
        <template #employee="{ row }">
          <div class="table-default__left">
            <router-link
              :to="{ name: 'employees-profile', params: { id: row.employee.id } }"
              class="table-default__link-border"
            >
              {{ row.employee.full_name }}
            </router-link>
          </div>
        </template>
        <template #amount="{ row }">{{ row.amount.toLocaleString() }}</template>
        <template #installments="{ row }">{{ row.installments }}</template>
        <template #paid="{ row }">{{ row.paid_amount.toLocaleString() }}</template>
        <template #remaining="{ row }">{{ row.remain_installments }}</template>
        <template #unpaid="{ row }">{{ row.unpaid_amount.toLocaleString() }}</template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records
          :all="pagination.count"
          :count="table.items.length"
          :page="pagination.page"
        />
      </app-cells>
    </template>
  </div>
</template>

<script>
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import { Chart } from 'highcharts-vue'
import moment from 'moment'
import {
  getEmployeesAnalyticsCompensationTZS,
  getEmployeesAnalyticsCompensationAdvances,
  getEmployeesAdvanceRequestsList,
} from '@/http'

export default {
  name: 'EmployeesAnalyticsCompensation',
  components: { VueCtkDateTimePicker, highcharts: Chart },
  data() {
    return {
      is_loaded: false,
      filter_dates: null,
      date_picker_error: false,
      custom_shortcuts: [
        {
          key: 'next30Days',
          label: 'Next 30 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(30, 'days')
            }
          }
        },
        {
          key: 'next60Days',
          label: 'Next 60 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(60, 'days')
            }
          }
        },
        {
          key: 'next90Days',
          label: 'Next 90 days',
          value: () => {
            return {
              start: moment(),
              end: moment().add(90, 'days')
            }
          }
        },
        { key: 'last30Days', label: 'Last 30 days', value: 30 },
        { key: 'thisYear', label: 'This year', value: 'year' },
      ],
      graph_options: {
        accessibility: {
          enabled: false,
        },
        CSSObject: {
          fontFamily: 'regular',
          fontSize: '6.2em',
        },
        chart: {
          type: 'spline'
        },
        title: {
          text: ''
        },
        xAxis: {
          categories: [],
        },
        yAxis: {
          title: {
            text: ''
          }
        },
        plotOptions: {
          series: {
            dataLabels: {
              enabled: true,
            }
          }
        },
        series: []
      },
      graph_options_advances: {
        accessibility: {
          enabled: false,
        },
        CSSObject: {
          fontFamily: 'regular',
          fontSize: '6.2em',
        },
        chart: {
          type: 'spline'
        },
        title: {
          text: ''
        },
        xAxis: {
          categories: [],
        },
        yAxis: {
          title: {
            text: ''
          }
        },
        plotOptions: {
          series: {
            dataLabels: {
              enabled: true,
            }
          }
        },
        series: []
      },
      table: {
        items: [],
        columns: ['employee', 'amount', 'installments', 'paid', 'remaining', 'unpaid'],
        options: {
          headings: {
            employee: 'Employee',
            amount: 'Amount',
            installments: 'Installments',
            paid: 'Paid',
            remaining: 'Remaining installments',
            unpaid: 'Unpaid',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader: false,
    }
  },
  mounted() {
    this.fetchItems()
    Promise.all([
      this.fetchGraph(),
      this.fetchGraphAdvances(),
    ]).then(() => { this.is_loaded = true })
  },
  methods: {
    async fetchGraph() {
      const response = await getEmployeesAnalyticsCompensationTZS(this.filter_dates.start, this.filter_dates.end)
      this.graph_options.xAxis.categories = response.data.graph.categories
      this.graph_options.series = response.data.graph.series
    },
    async fetchGraphAdvances() {
      const response = await getEmployeesAnalyticsCompensationAdvances(this.filter_dates.start, this.filter_dates.end)
      this.graph_options_advances.xAxis.categories = response.data.graph.categories
      this.graph_options_advances.series = response.data.graph.series
    },
    onIsHiddenPicker() {
      if (this.filter_dates?.start && this.filter_dates?.end) {
        this.date_picker_error = false
        this.fetchGraph()
        this.fetchGraphAdvances()
      } else this.date_picker_error = true
    },
    fetchItems() {
      this.table_preloader = true
      getEmployeesAdvanceRequestsList({ page: this.pagination.page, page_size: 20, status: 'paid', paid: false })
        .then(res => {
          this.table.items = res.data.results
          this.pagination.pages = res.data.pages
          this.pagination.count = res.data.count
          this.table_preloader = false
        })
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
  }
}
</script>
